<script setup>
// ============================================================================
// BonBoZ: trang Thống kê > NHÂN VIÊN — dựng lại theo đúng bố cục Pancake.
//
// BỐ CỤC, từ trên xuống:
//   1. Top 5 nhân viên phản hồi nhanh nhất — thanh ngang, huy chương cho 3 hạng
//   2. Tỉ lệ phản hồi theo nhân viên       — vòng tròn + danh sách bên phải
//   3. Thống kê chi tiết                   — bảng 10 cột, tab [Nhân viên]
//                                            [Thời gian] + Tuỳ chỉnh + lọc
//
// QUY CÔNG THEO NGƯỜI ĐÃ NHẮN, không theo người đang giữ hội thoại — người trả
// lời hộ lúc đồng nghiệp nghỉ vẫn phải có số. Lý do đầy đủ ở đầu
// `Bonboz::HieuSuatNhanVienBuilder`.
//
// "PHẢN HỒI NHANH NHẤT" xếp theo TRUNG BÌNH, khớp nhãn "TB" của Pancake — nhưng
// trung bình bị một ca khách nhắn đêm kéo lên hàng giờ. Trung vị nằm trong chú
// giải của từng ô để đối chiếu; trên máy dev hai con số là 4 giờ 8 phút so với
// 7 phút, cùng một người cùng một kỳ.
//
// GIỮ LẠI SO VỚI PANCAKE, vì người dùng đã yêu cầu riêng trước đó: dải chọn
// khoảng ngày và `PhamVi` (đang xem những trang nào).
// ============================================================================
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import HieuSuatAPI from 'dashboard/api/bonboz/hieuSuat';
import { useAlert } from 'dashboard/composables';
import { useSelectedPages } from 'dashboard/composables/useSelectedPages';
import { dinhDangThoiLuong } from 'dashboard/helper/thoiLuong';
import { mauCss } from 'dashboard/helper/mauBieuDo';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import BieuDoTron from './BieuDoTron.vue';
import BangNhanVien from './BangNhanVien.vue';
import SectionCard from './SectionCard.vue';
import ReportHeader from '../components/ReportHeader.vue';
import OverviewReportFilters from '../components/OverviewReportFilters.vue';
import PhamVi from './PhamVi.vue';

const { t } = useI18n();
// Bang chi duoc noi ve nhung trang dang chon o bang dieu khien.
const { selectedInboxIds } = useSelectedPages();

const dangTai = ref(true);
const dangTaiBang = ref(false);
const khoang = ref(null);
const gom = ref('nhan_vien');

// Số theo NGƯỜI luôn được tải: hai khối trên đầu đọc từ đây, kể cả khi bảng
// đang ở tab Thời gian.
const theoNguoi = ref(null);
// Số theo NGÀY chỉ tải khi bấm sang tab đó — 30 dòng nữa cho một tab phần lớn
// thời gian không ai mở.
const theoThoiGian = ref(null);

const goi = kieu =>
  HieuSuatAPI.nhanVien({
    since: khoang.value.from,
    until: khoang.value.to,
    inboxIds: selectedInboxIds.value,
    gom: kieu,
  }).then(r => r.data);

const taiThoiGian = async () => {
  if (theoThoiGian.value || !khoang.value) return;
  dangTaiBang.value = true;
  try {
    theoThoiGian.value = await goi('thoi_gian');
  } catch {
    theoThoiGian.value = null;
    gom.value = 'nhan_vien';
    useAlert(t('BONBOZ.NHAN_VIEN.LOI_TAI'));
  } finally {
    dangTaiBang.value = false;
  }
};

const tai = async () => {
  if (!khoang.value) return;
  dangTai.value = true;
  try {
    theoNguoi.value = await goi('nhan_vien');
    // Số của kỳ cũ không còn đúng sau khi đổi ngày hay đổi trang.
    theoThoiGian.value = null;
    if (gom.value === 'thoi_gian') await taiThoiGian();
  } catch {
    theoNguoi.value = null;
    useAlert(t('BONBOZ.NHAN_VIEN.LOI_TAI'));
  } finally {
    dangTai.value = false;
  }
};

const doiGom = async kieu => {
  gom.value = kieu;
  if (kieu === 'thoi_gian') await taiThoiGian();
};

const doiBoLoc = ({ from, to }) => {
  if (!from || !to) return;
  khoang.value = { from, to };
  tai();
};

// Lua chon trang nam trong `ui_settings` cua nguoi dung, tai ve SAU khi trang
// da mount — lan tai dau tien co the chay khi mang con rong (= tat ca).
watch(selectedInboxIds, tai);

const nguoi = computed(() => theoNguoi.value?.nhan_vien || []);

// ------------------------------------------------- 1. phản hồi nhanh nhất
// Chỉ xếp hạng người CÓ số phản hồi: người chưa trả lời hội thoại nào không
// phải "nhanh vô hạn", họ chỉ là chưa có mặt trong phép đo này.
const nhanhNhat = computed(() =>
  nguoi.value
    .filter(n => n.phan_hoi_tb !== null && n.phan_hoi_tb !== undefined)
    .sort((a, b) => a.phan_hoi_tb - b.phan_hoi_tb)
    .slice(0, 5)
);
const chamNhat = computed(() =>
  Math.max(1, ...nhanhNhat.value.map(n => n.phan_hoi_tb))
);

// Ba hạng đầu đeo huy chương, đúng như Pancake. Huy chương cho tất cả thì không
// còn là huy chương.
const MAU_HUY_CHUONG = ['#E3A008', '#9CA3AF', '#B45309'];
const huyChuong = i => MAU_HUY_CHUONG[i] || null;

// --------------------------------------------------- 2. tỉ lệ phản hồi
// Phần chia của mỗi người trên TỔNG tin nhắn + bình luận của cả bảng. Cùng công
// thức với cột "T.lệ ph.hồi" — một chỗ đổi thì cả hai đổi theo, vì cả hai đọc
// từ `tiLePhanHoi`.
const tongPhanHoi = computed(() =>
  nguoi.value.reduce((s, n) => s + n.tin_nhan + n.binh_luan, 0)
);
const tiLePhanHoi = computed(() =>
  nguoi.value
    .map(n => ({
      ...n,
      phan: tongPhanHoi.value
        ? ((n.tin_nhan + n.binh_luan) / tongPhanHoi.value) * 100
        : 0,
    }))
    .filter(n => n.phan > 0)
    .sort((a, b) => b.phan - a.phan)
);

// Bảng màu xoay vòng cho vòng tròn. Dùng biến theme chứ không mã màu cứng của
// Pancake: sản phẩm này có nền tối, mã cứng thì nền tối đọc không ra.
const MAU_VONG = [
  '--bonboz-cho',
  '--bonboz-mo',
  '--bonboz-xong',
  '--bonboz-nguoi',
  '--bonboz-tre',
];
const mauCua = i => MAU_VONG[i % MAU_VONG.length];

// `mauCss` đọc biến CSS ra chuỗi `rgb(...)`: canvas KHÔNG hiểu `var(--x)`, đặt
// thẳng vào là miếng bánh ra đen. Xem đầu `helper/mauBieuDo.js`.
const miengVong = computed(() =>
  tiLePhanHoi.value.map((n, i) => ({
    tag: n.ten,
    so: n.phan,
    nen: mauCss(mauCua(i)),
  }))
);

// ------------------------------------------------------------------ bảng
const nguonBang = computed(() =>
  gom.value === 'thoi_gian' ? theoThoiGian.value : theoNguoi.value
);
</script>

<template>
  <ReportHeader
    :header-title="$t('BONBOZ.NHAN_VIEN.TIEU_DE')"
    :header-description="$t('BONBOZ.NHAN_VIEN.MO_TA')"
  />

  <div class="flex flex-col w-full gap-4 mx-auto max-w-6xl">
    <OverviewReportFilters :disabled="dangTai" @filter-change="doiBoLoc" />

    <PhamVi />

    <div class="grid gap-4 lg:grid-cols-2 lg:items-start">
      <!-- ============ 1. PHẢN HỒI NHANH NHẤT ============ -->
      <SectionCard
        :title="$t('BONBOZ.NHAN_VIEN.TOP_NHANH')"
        :description="$t('BONBOZ.NHAN_VIEN.TOP_NHANH_MO_TA')"
      >
        <div
          v-if="dangTai"
          class="h-56 rounded-lg bg-n-alpha-2 animate-pulse"
        />
        <p
          v-else-if="!nhanhNhat.length"
          class="m-0 py-16 text-sm text-center text-n-slate-11"
        >
          {{ $t('BONBOZ.NHAN_VIEN.CHUA_CO') }}
        </p>
        <ul v-else class="flex flex-col gap-3 p-0 m-0 list-none">
          <li
            v-for="(n, i) in nhanhNhat"
            :key="n.id"
            v-tooltip.top="
              $t('BONBOZ.NHAN_VIEN.GT_TRUNG_VI', {
                tv: dinhDangThoiLuong(n.phan_hoi_tv),
              })
            "
            class="flex items-center gap-2"
          >
            <Avatar :src="n.anh" :name="n.ten" :size="32" rounded-full />
            <span class="w-28 text-sm truncate shrink-0 text-n-slate-12">
              {{ n.ten }}
            </span>
            <span
              v-if="huyChuong(i)"
              class="block i-lucide-medal size-4 shrink-0"
              :style="{ color: huyChuong(i) }"
              aria-hidden="true"
            />
            <span v-else class="size-4 shrink-0" aria-hidden="true" />
            <!-- Thanh dài theo thời gian: NGẮN là nhanh. Ngược với trực giác
                 "dài hơn là hơn", nên tiêu đề khối phải nói rõ "nhanh nhất". -->
            <span class="flex items-center flex-1 gap-2 min-w-0">
              <span
                class="h-6 rounded bg-[rgb(var(--bonboz-mo)/0.18)]"
                :style="{
                  width: `${Math.max(4, (n.phan_hoi_tb / chamNhat) * 100)}%`,
                }"
              />
              <span class="text-xs whitespace-nowrap text-n-slate-11">
                {{ dinhDangThoiLuong(n.phan_hoi_tb) }}
              </span>
            </span>
          </li>
        </ul>
      </SectionCard>

      <!-- ============ 2. TỈ LỆ PHẢN HỒI ============ -->
      <SectionCard
        :title="$t('BONBOZ.NHAN_VIEN.TI_LE')"
        :description="$t('BONBOZ.NHAN_VIEN.TI_LE_MO_TA')"
      >
        <div
          v-if="dangTai"
          class="h-56 rounded-lg bg-n-alpha-2 animate-pulse"
        />
        <p
          v-else-if="!tiLePhanHoi.length"
          class="m-0 py-16 text-sm text-center text-n-slate-11"
        >
          {{ $t('BONBOZ.NHAN_VIEN.CHUA_CO') }}
        </p>
        <div v-else class="flex flex-wrap items-center gap-4">
          <div class="relative size-40 shrink-0">
            <div class="absolute inset-0">
              <BieuDoTron :dong="miengVong" />
            </div>
          </div>
          <ul class="flex flex-col flex-1 gap-2 p-0 m-0 list-none min-w-48">
            <li
              v-for="(n, i) in tiLePhanHoi"
              :key="n.id"
              class="flex items-center gap-2"
            >
              <span
                class="h-2 rounded-full w-5 shrink-0"
                :style="{ background: `rgb(var(${mauCua(i)}))` }"
                aria-hidden="true"
              />
              <Avatar :src="n.anh" :name="n.ten" :size="20" rounded-full />
              <span class="flex-1 text-sm truncate text-n-slate-12">
                {{ n.ten }}
              </span>
              <span class="text-sm tabular-nums shrink-0 text-n-slate-11">
                {{ Math.round(n.phan * 100) / 100 }}%
              </span>
            </li>
          </ul>
        </div>
      </SectionCard>
    </div>

    <!-- ============ 3. THỐNG KÊ CHI TIẾT ============ -->
    <BangNhanVien
      :dong="nguonBang?.nhan_vien || []"
      :tong="nguonBang?.tong || null"
      :gom="gom"
      :dang-tai="dangTai || dangTaiBang"
      @doi-gom="doiGom"
    />
  </div>
</template>
